import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import defaultImage from 'src/assets/images/default-icon.png';
import { DEPTH } from 'src/common/constants';
import { currencyFormatter, isEmptyObj, isValidRegex } from 'src/common/helpers';
import { NoRecord, SortDown, SortUp } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import { Position, TradeSocketResponseData } from 'src/types';
import { FutureSymbol, TokenObj } from 'src/types/configV2';

import {
    DrawerCoinListContainer,
    DrawerHoldingCoinListHeader,
    DrawerHoldingCoinListItem,
    NoRecordContainer,
} from './styles';

interface HoldingProps {
    search: string;
    handleCloseDrawer: () => void;
}

export interface TradeSocketFutureData extends TradeSocketResponseData, FutureSymbol, Position {
    [key: string]: any;
}
export interface TradeSocketFutureDataObj {
    [key: string]: TradeSocketFutureData;
}

type FilterKey = 'TRADING_PAIR' | 'PRICE' | 'CHANGE' | 'VALUE' | 'QUANTITY';

type FilterValue = {
    direction: 'ASC' | 'DESC';
    key: string;
};

type FILTER_TYPES = {
    // eslint-disable-next-line no-unused-vars
    [key in FilterKey]?: FilterValue;
};

const FILTER_DIRECTION = {
    ASC: 'ASC',
    DESC: 'DESC',
};

const FILTERS = {
    PRICE: 'PRICE' as keyof FILTER_TYPES,
    CHANGE: 'CHANGE' as keyof FILTER_TYPES,
    VALUE: 'VALUE' as keyof FILTER_TYPES,
    QUANTITY: 'QUANTITY' as keyof FILTER_TYPES,
    TRADING_PAIR: 'TRADING_PAIR' as keyof FILTER_TYPES,
};

const Holding: FC<HoldingProps> = ({ search, handleCloseDrawer }) => {
    const { t } = useTranslation();
    const { colors, isMobile } = useTheme();
    const navigate = useNavigate();

    const { futuresSymbol, token } = useAppSelector((state) => state.global.configV2);
    const { positions } = useAppSelector((state) => state.derivatives);
    const slowBroker = useAppSelector((state) => state.socket.slowBroker);
    const fetchUserFeaturesConfigLoading = useAppSelector(
        (state) => state.user.loading.fetchUserFeaturesConfig,
    );

    const [activeFilter, setActiveFilter] = useState({
        [FILTERS.VALUE]: { direction: 'DESC', key: 'positionValues' },
    } as FILTER_TYPES);
    const [filteredSymbols, setFilteredSymbols] = useState({} as TradeSocketFutureDataObj);

    const setFilter = (filterKey: string, key: string) => {
        let direction = FILTER_DIRECTION.ASC;
        const currentDirection = activeFilter[filterKey as FilterKey]?.direction;
        if (currentDirection === FILTER_DIRECTION.DESC) {
            setActiveFilter({});
            return;
        }
        if (currentDirection === FILTER_DIRECTION.ASC) {
            direction = FILTER_DIRECTION.DESC;
        } else {
            direction = FILTER_DIRECTION.ASC;
        }
        setActiveFilter({
            [filterKey]: { direction, key },
        });
    };

    useEffect(() => {
        let symbolNeedsToUpdate = {} as TradeSocketFutureDataObj;
        let isValidSearch: RegExpMatchArray | null = null;
        let currentFilter = Object.keys(activeFilter);

        if (fetchUserFeaturesConfigLoading) {
            return setFilteredSymbols({});
        }

        if (isValidRegex(search)) {
            isValidSearch = search.match(new RegExp('^[a-zA-Z0-9-]*$'));
        }
        Object.values(positions).map((position: Position) => {
            let matchArr =
                isValidSearch !== null ? position.symbolName.match(new RegExp(search, 'ig')) : null;
            if (matchArr !== null && matchArr.length > 0 && isValidSearch !== null) {
                symbolNeedsToUpdate[position.symbolId] = {
                    ...position,
                    ...slowBroker[position.symbolId],
                    ...futuresSymbol[position.symbolId],
                };
            }
        });
        if (currentFilter.length > 0) {
            let typedKey = currentFilter[0] as keyof FILTER_TYPES;
            let key = activeFilter[typedKey]?.key || '';
            let sortDirection = activeFilter[typedKey]?.direction || '';
            if (typedKey === FILTERS.TRADING_PAIR) {
                symbolNeedsToUpdate = Object.entries(symbolNeedsToUpdate)
                    .sort((a, b) =>
                        sortDirection === FILTER_DIRECTION.ASC
                            ? a[1][key].localeCompare(b[1][key])
                            : b[1][key].localeCompare(a[1][key]),
                    )
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
            } else {
                symbolNeedsToUpdate = Object.entries(symbolNeedsToUpdate)
                    .sort((a, b) => {
                        return sortDirection === FILTER_DIRECTION.ASC
                            ? a[1][key] - b[1][key]
                            : b[1][key] - a[1][key];
                    })
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
            }
        }
        setFilteredSymbols(symbolNeedsToUpdate);
    }, [
        futuresSymbol,
        positions,
        search,
        slowBroker,
        activeFilter,
        fetchUserFeaturesConfigLoading,
    ]);

    return (
        <>
            <DrawerHoldingCoinListHeader>
                <FlexContainer>
                    <FlexContainer
                        onClick={() => setFilter(FILTERS.TRADING_PAIR, 'symbolName')}
                        styles={{ gap: 4, cursor: 'pointer' }}
                    >
                        {t('trading_pairs')}
                        <FlexContainer styles={{ flexDirection: 'column' }}>
                            <SortUp
                                fill={
                                    activeFilter?.TRADING_PAIR?.direction === FILTER_DIRECTION.ASC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                            <SortDown
                                fill={
                                    activeFilter?.TRADING_PAIR?.direction === FILTER_DIRECTION.DESC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
                <FlexContainer styles={{ cursor: 'pointer', gap: 4 }}>
                    <FlexContainer
                        onClick={() => setFilter(FILTERS.VALUE, 'positionValues')}
                        styles={{ gap: 4 }}
                    >
                        {t('value')}
                        <FlexContainer styles={{ flexDirection: 'column' }}>
                            <SortUp
                                fill={
                                    activeFilter?.VALUE?.direction === FILTER_DIRECTION.ASC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                            <SortDown
                                fill={
                                    activeFilter?.VALUE?.direction === FILTER_DIRECTION.DESC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                        </FlexContainer>
                    </FlexContainer>
                    {'/'}
                    <FlexContainer
                        onClick={() => setFilter(FILTERS.QUANTITY, 'total')}
                        styles={{ gap: 4 }}
                    >
                        {t('qty')}
                        <FlexContainer styles={{ flexDirection: 'column' }}>
                            <SortUp
                                fill={
                                    activeFilter?.QUANTITY?.direction === FILTER_DIRECTION.ASC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                            <SortDown
                                fill={
                                    activeFilter?.QUANTITY?.direction === FILTER_DIRECTION.DESC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
                <FlexContainer styles={{ cursor: 'pointer', gap: 4 }}>
                    <FlexContainer
                        onClick={() => setFilter(FILTERS.PRICE, 'c')}
                        styles={{ gap: 4 }}
                    >
                        {t('price')}
                        <FlexContainer styles={{ flexDirection: 'column' }}>
                            <SortUp
                                fill={
                                    activeFilter?.PRICE?.direction === FILTER_DIRECTION.ASC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                            <SortDown
                                fill={
                                    activeFilter?.PRICE?.direction === FILTER_DIRECTION.DESC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                        </FlexContainer>
                    </FlexContainer>
                    {'/'}
                    <FlexContainer
                        onClick={() => setFilter(FILTERS.CHANGE, 'm')}
                        styles={{ gap: 4 }}
                    >
                        {`${t('24h_change')} ${!isMobile ? '%' : ''}`}
                        <FlexContainer styles={{ flexDirection: 'column' }}>
                            <SortUp
                                fill={
                                    activeFilter?.CHANGE?.direction === FILTER_DIRECTION.ASC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                            <SortDown
                                fill={
                                    activeFilter?.CHANGE?.direction === FILTER_DIRECTION.DESC
                                        ? colors.primary
                                        : colors.text.tertiary
                                }
                            />
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
            </DrawerHoldingCoinListHeader>
            {!isEmptyObj(filteredSymbols) && (
                <DrawerCoinListContainer>
                    {Object.keys(filteredSymbols).map((symbolId: string, index) => {
                        const symbol: TradeSocketFutureData = filteredSymbols[symbolId];
                        return (
                            <DrawerHoldingCoinListItem
                                key={index}
                                styles={{ cursor: 'pointer' }}
                                onClick={() => {
                                    navigate(`${APP_URLS.CONTRACT}/${symbolId}`);
                                    handleCloseDrawer();
                                }}
                            >
                                <FlexContainer styles={{ gap: 8 }}>
                                    {!isMobile && (
                                        <img
                                            src={
                                                token[
                                                    symbol.secondLevelUnderlyingId as keyof TokenObj
                                                ]?.iconUrl || defaultImage
                                            }
                                            alt={symbolId}
                                        />
                                    )}
                                    {symbol.symbolName}
                                </FlexContainer>
                                <FlexContainer
                                    styles={{ flexDirection: 'column', alignItems: 'flex-end' }}
                                >
                                    <span>
                                        {currencyFormatter(
                                            Number(symbol.positionValues),
                                            DEPTH[symbol?.minPricePrecision],
                                        )}
                                    </span>
                                    <span>
                                        {currencyFormatter(
                                            Number(symbol.available),
                                            DEPTH[symbol?.basePrecision],
                                        )}
                                    </span>
                                </FlexContainer>
                                <FlexContainer
                                    styles={{ flexDirection: 'column', alignItems: 'flex-end' }}
                                >
                                    <span>
                                        {symbol.c
                                            ? currencyFormatter(
                                                  Number(symbol.c),
                                                  DEPTH[symbol?.minPricePrecision],
                                              )
                                            : '--'}
                                    </span>
                                    <span className={Number(symbol?.m) >= 0 ? 'up' : 'down'}>
                                        {Number(symbol?.m) > 0 && '+'}
                                        {Number(symbol?.m)
                                            ? `${currencyFormatter(
                                                  Math.floor(Number(symbol.m) * 10000) / 100,
                                                  2,
                                              )}%`
                                            : '0.00%'}
                                    </span>
                                </FlexContainer>
                            </DrawerHoldingCoinListItem>
                        );
                    })}
                </DrawerCoinListContainer>
            )}
            {isEmptyObj(filteredSymbols) && (
                <NoRecordContainer>
                    <NoRecord fill={colors.icon.default} stroke={colors.icon.default} />
                    <p>{t(search !== '' ? 'no_result' : 'no_record')}</p>
                </NoRecordContainer>
            )}
        </>
    );
};

export { Holding };
